/**
 * Leave Balance Adjustments — Table Workspace (Phase 1, Jul 2026).
 * Compact server-side paginated table: expandable rows for full balance breakdown,
 * right-side drawer for adjustments. Permissions come from the capabilities endpoint.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, RotateCcw, Loader2, History, ChevronLeft, ChevronRight,
  ChevronDown, ChevronRight as ChevronRightSm, Pencil,
  Users, TrendingDown, CircleSlash, AlertTriangle, CalendarClock
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import AdjustmentHistoryDrawer from './AdjustmentHistoryDrawer';
import LeaveAdjustmentDrawer from './LeaveAdjustmentDrawer';
import { formatDateTimeLocal } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_BADGE = {
  normal: { cls: 'bg-emerald-50 text-emerald-700', label: 'Normal' },
  low: { cls: 'bg-amber-50 text-amber-700', label: 'Low' },
  zero: { cls: 'bg-slate-100 text-slate-600', label: 'Zero' },
  negative: { cls: 'bg-rose-50 text-rose-700', label: 'Negative' },
};

// YEAR-SELECTOR-001: fallback only until /adjustments-filters supplies
// data-driven years (current + past-with-data, never future).
const YEARS_FALLBACK = [new Date().getFullYear()];
const DEFAULT_FILTERS = { search: '', team: 'all', manager: 'all', employment_status: 'all', balance_status: 'all' };

const fmtDT = (d) => d ? formatDateTimeLocal(d) : '—';

const SummaryCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white rounded-lg border border-slate-200 px-4 py-3 shadow-sm flex items-center gap-3">
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}><Icon className="w-4 h-4" /></div>
    <div>
      <p className="text-xl font-bold text-slate-900 leading-tight">{value}</p>
      <p className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</p>
    </div>
  </div>
);

const DetailStat = ({ label, value, valueCls = '' }) => (
  <div className="bg-white rounded-md border border-slate-200 px-3 py-2">
    <p className="text-[10px] text-slate-400 uppercase tracking-wide">{label}</p>
    <p className={`text-sm font-semibold text-slate-900 ${valueCls}`}>{value}</p>
  </div>
);

export const BalanceAdjustments = () => {
  const [caps, setCaps] = useState(null);
  const [options, setOptions] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [leaveYear, setLeaveYear] = useState(new Date().getFullYear());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('employee_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandDetail, setExpandDetail] = useState({}); // user_id -> {loading, adjustments}
  const [adjustRow, setAdjustRow] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);
  const [highlightRow, setHighlightRow] = useState(null);
  const searchTimer = useRef(null);
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [c, o] = await Promise.all([
          axios.get(`${API}/api/leave-registry/adjustments-capabilities`),
          axios.get(`${API}/api/leave-registry/adjustments-filters`),
        ]);
        setCaps(c.data);
        setOptions(o.data);
        if (o.data.leave_types?.length) setLeaveTypeId(o.data.leave_types[0].id);
      } catch (e) { toast.error('Failed to load adjustment workspace'); }
    })();
  }, []);

  const fetchTable = useCallback(async () => {
    if (!leaveTypeId) return;
    setLoading(true);
    try {
      const params = {
        page, page_size: pageSize, leave_type_id: leaveTypeId, leave_year: leaveYear,
        sort_by: sortBy, sort_order: sortOrder,
      };
      if (filters.search) params.search = filters.search;
      if (filters.team !== 'all') params.team = filters.team;
      if (filters.manager !== 'all') params.manager = filters.manager;
      if (filters.employment_status !== 'all') params.employment_status = filters.employment_status;
      if (filters.balance_status !== 'all') params.balance_status = filters.balance_status;
      const res = await axios.get(`${API}/api/leave-registry/adjustments-table`, { params });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load balances');
    } finally { setLoading(false); }
  }, [page, pageSize, leaveTypeId, leaveYear, sortBy, sortOrder, filters]);

  useEffect(() => { fetchTable(); }, [fetchTable]);

  useEffect(() => { setExpandedRow(null); setExpandDetail({}); }, [leaveTypeId, leaveYear, page, filters]);

  const onSearchChange = (v) => {
    setSearchInput(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setFilters(f => ({ ...f, search: v })); setPage(1); }, 400);
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS); setSearchInput(''); setPage(1);
    setLeaveYear(new Date().getFullYear());
    if (options?.leave_types?.length) setLeaveTypeId(options.leave_types[0].id);
  };

  const toggleExpand = async (row) => {
    const uid = row.user_id;
    if (expandedRow === uid) { setExpandedRow(null); return; }
    setExpandedRow(uid);
    if (!expandDetail[uid]) {
      setExpandDetail(prev => ({ ...prev, [uid]: { loading: true, adjustments: [] } }));
      try {
        const res = await axios.get(`${API}/api/leave-registry/adjustments`, {
          params: { user_id: uid, leave_type_id: leaveTypeId, year: leaveYear, page: 1, page_size: 3 }
        });
        setExpandDetail(prev => ({ ...prev, [uid]: { loading: false, adjustments: res.data.adjustments || [], total: res.data.total_items } }));
      } catch (e) {
        setExpandDetail(prev => ({ ...prev, [uid]: { loading: false, adjustments: [], error: true } }));
      }
    }
  };

  const onAdjustmentSaved = (uid) => {
    setAdjustRow(null);
    if (uid) {
      setHighlightRow(uid);
      setTimeout(() => setHighlightRow(null), 2500);
    }
    setExpandDetail({});
    fetchTable();
  };

  if (!caps || !options) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  const canAdjust = caps.can_adjust;

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-4" data-testid="balance-adjustments-workspace">
      {/* Summary cards */}
      {data?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="adjustment-summary-cards">
          <SummaryCard icon={Users} label="Employees (filtered)" value={data.summary.total_employees} accent="bg-blue-50 text-blue-600" />
          <SummaryCard icon={TrendingDown} label="Low balance" value={data.summary.low_balance} accent="bg-amber-50 text-amber-600" />
          <SummaryCard icon={CircleSlash} label="Zero balance" value={data.summary.zero_balance} accent="bg-slate-100 text-slate-600" />
          <SummaryCard icon={AlertTriangle} label="Negative balance" value={data.summary.negative_balance} accent="bg-rose-50 text-rose-600" />
          <SummaryCard icon={CalendarClock} label="Adjustments today" value={data.summary.adjustments_today} accent="bg-emerald-50 text-emerald-600" />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 flex flex-wrap items-center gap-2" data-testid="adjustment-filters">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={searchInput} onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search name or email..." className="pl-8 w-52 h-9" data-testid="adj-search-input" />
        </div>
        <Select value={leaveTypeId} onValueChange={(v) => { setLeaveTypeId(v); setPage(1); }}>
          <SelectTrigger className="w-40 h-9" data-testid="adj-leave-type-filter"><SelectValue placeholder="Leave type" /></SelectTrigger>
          <SelectContent>{options.leave_types.map(t => <SelectItem key={t.id} value={t.id}>{t.name}{t.abbreviation ? ` (${t.abbreviation})` : ''}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(leaveYear)} onValueChange={(v) => { setLeaveYear(parseInt(v)); setPage(1); }}>
          <SelectTrigger className="w-24 h-9" data-testid="adj-year-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{(options.years?.length ? options.years : YEARS_FALLBACK).map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.team} onValueChange={(v) => { setFilters(f => ({ ...f, team: v })); setPage(1); }}>
          <SelectTrigger className="w-36 h-9" data-testid="adj-team-filter"><SelectValue placeholder="Team" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All teams</SelectItem>
            {options.teams.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.manager} onValueChange={(v) => { setFilters(f => ({ ...f, manager: v })); setPage(1); }}>
          <SelectTrigger className="w-40 h-9" data-testid="adj-manager-filter"><SelectValue placeholder="Manager" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All managers</SelectItem>
            {options.managers.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.employment_status} onValueChange={(v) => { setFilters(f => ({ ...f, employment_status: v })); setPage(1); }}>
          <SelectTrigger className="w-36 h-9" data-testid="adj-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>
            {options.employment_statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.balance_status} onValueChange={(v) => { setFilters(f => ({ ...f, balance_status: v })); setPage(1); }}>
          <SelectTrigger className="w-36 h-9" data-testid="adj-balance-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All balances</SelectItem>
            <SelectItem value="low">Low balance</SelectItem>
            <SelectItem value="zero">Zero balance</SelectItem>
            <SelectItem value="negative">Negative balance</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={resetFilters} className="h-9 gap-1" data-testid="adj-reset-filters">
          <RotateCcw className="w-3.5 h-3.5" /> Reset
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm tf-blue-head">
            <thead className="tf-thead">
              <tr>
                <th className="sticky left-0 z-10 px-3 py-2.5 text-left text-xs font-semibold whitespace-nowrap cursor-pointer"
                  onClick={() => { setSortBy('employee_name'); setSortOrder(o => (sortBy === 'employee_name' && o === 'asc') ? 'desc' : 'asc'); }}>
                  Employee{sortBy === 'employee_name' && (sortOrder === 'asc' ? ' ▲' : ' ▼')}
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold whitespace-nowrap">Team</th>
                <th className="px-3 py-2.5 text-center text-xs font-semibold whitespace-nowrap cursor-pointer"
                  onClick={() => { setSortBy('available_balance'); setSortOrder(o => (sortBy === 'available_balance' && o === 'asc') ? 'desc' : 'asc'); }}>
                  Available Balance{sortBy === 'available_balance' && (sortOrder === 'asc' ? ' ▲' : ' ▼')}
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold whitespace-nowrap">Status</th>
                <th className="sticky right-0 z-10 px-3 py-2.5 text-center text-xs font-semibold whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" /></td></tr>
              ) : data?.items?.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-slate-400 text-sm" data-testid="adj-empty-state">No employees match the selected filters.</td></tr>
              ) : data?.items?.map(row => {
                const sb = STATUS_BADGE[row.balance_status] || STATUS_BADGE.normal;
                const isExpanded = expandedRow === row.user_id;
                const rowBg = highlightRow === row.user_id ? 'bg-emerald-50' : isExpanded ? 'bg-blue-50/50' : 'bg-white';
                const stickyBg = highlightRow === row.user_id ? 'bg-emerald-50' : isExpanded ? 'bg-blue-50' : 'bg-white group-hover:bg-slate-50';
                const detail = expandDetail[row.user_id];
                return (
                  <React.Fragment key={row.user_id}>
                    <tr className={`group border-t border-slate-100 transition-colors ${rowBg} ${!isExpanded && highlightRow !== row.user_id ? 'hover:bg-slate-50' : ''}`}
                      data-testid={`adj-row-${row.user_id}`}>
                      <td className={`sticky left-0 z-[1] px-3 py-2 whitespace-nowrap transition-colors ${stickyBg}`}>
                        <div className="flex items-center gap-1.5">
                          <button type="button" onClick={() => toggleExpand(row)}
                            aria-expanded={isExpanded} aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                            className="p-1 rounded hover:bg-slate-200 text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400"
                            data-testid={`adj-expand-${row.user_id}`}>
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRightSm className="w-4 h-4" />}
                          </button>
                          <div>
                            <p className="font-medium text-slate-900">{row.employee_name}</p>
                            <p className="text-xs text-slate-400">{row.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{row.team || '—'}</td>
                      <td className={`px-3 py-2 text-center font-bold ${row.available_balance < 0 ? 'text-rose-600' : 'text-slate-900'}`}
                        data-testid={`adj-balance-${row.user_id}`}>{row.available_balance}</td>
                      <td className="px-3 py-2"><Badge className={`border-0 text-[11px] ${sb.cls}`}>{sb.label}</Badge></td>
                      <td className={`sticky right-0 z-[1] px-3 py-2 transition-colors ${stickyBg}`}>
                        <div className="flex items-center justify-center gap-1">
                          {canAdjust && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-50"
                                  onClick={(e) => { e.stopPropagation(); setAdjustRow(row); }}
                                  aria-label={`Adjust balance for ${row.employee_name}`}
                                  data-testid={`adj-open-drawer-${row.user_id}`}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="left">Adjust balance</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:bg-slate-100"
                                onClick={(e) => { e.stopPropagation(); setHistoryRow(row); }}
                                aria-label={`Adjustment history for ${row.employee_name}`}
                                data-testid={`adj-history-${row.user_id}`}>
                                <History className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="left">Adjustment history</TooltipContent>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-t border-blue-100 bg-blue-50/30" data-testid={`adj-detail-row-${row.user_id}`}>
                        <td colSpan={5} className="px-4 py-3">
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
                            <DetailStat label="Employee ID" value={row.employee_id || '—'} />
                            <DetailStat label="Entitlement" value={`${row.entitlement} d`} />
                            <DetailStat label="Used" value={`${row.used} d`} />
                            <DetailStat label="Pending" value={`${row.pending} d`} />
                            <DetailStat label="Adjustments" value={`${row.ledger_adjustments > 0 ? '+' : ''}${row.ledger_adjustments} d`}
                              valueCls={row.ledger_adjustments > 0 ? 'text-emerald-600' : row.ledger_adjustments < 0 ? 'text-rose-600' : ''} />
                            <DetailStat label="Available" value={`${row.available_balance} d`}
                              valueCls={row.available_balance < 0 ? 'text-rose-600' : 'text-emerald-700'} />
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Recent adjustments · {row.leave_type_name} · {leaveYear}</p>
                            {detail?.loading ? (
                              <div className="flex items-center gap-2 text-xs text-slate-400 py-1" data-testid={`adj-detail-loading-${row.user_id}`}>
                                <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600" /> Loading adjustments...
                              </div>
                            ) : detail?.error ? (
                              <p className="text-xs text-rose-500">Failed to load adjustments. Open history to retry.</p>
                            ) : detail?.adjustments?.length === 0 ? (
                              <p className="text-xs text-slate-400">No adjustments recorded for this leave type and year.</p>
                            ) : (
                              <div className="space-y-1">
                                {detail?.adjustments?.map(adj => (
                                  <div key={adj.id} className="text-xs bg-white border border-slate-200 rounded-md px-2.5 py-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                                    <span className={`font-semibold ${adj.adjustment >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                      {adj.adjustment >= 0 ? '+' : ''}{adj.adjustment}d
                                    </span>
                                    <span className="text-slate-600">{adj.reason}</span>
                                    {adj.notes && <span className="text-slate-400 italic">“{adj.notes}”</span>}
                                    <span className="text-slate-400 ml-auto whitespace-nowrap">{adj.adjusted_by_name} · {fmtDT(adj.created_at)}</span>
                                  </div>
                                ))}
                                {detail?.total > 3 && (
                                  <button type="button" onClick={() => setHistoryRow(row)}
                                    className="text-xs text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-400 rounded"
                                    data-testid={`adj-detail-view-all-${row.user_id}`}>
                                    View all {detail.total} adjustments →
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {data && (
          <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 border-t border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>Rows per page:</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(parseInt(v)); setPage(1); }}>
                <SelectTrigger className="w-16 h-7 text-xs" data-testid="adj-page-size"><SelectValue /></SelectTrigger>
                <SelectContent>{[25, 50, 100].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select>
              <span data-testid="adj-pagination-info">
                {data.total_items === 0 ? '0' : `${(data.page - 1) * data.page_size + 1}–${Math.min(data.page * data.page_size, data.total_items)}`} of {data.total_items}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-7" disabled={data.page <= 1}
                onClick={() => setPage(p => p - 1)} data-testid="adj-prev-page"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-xs text-slate-600 px-2">Page {data.page} / {data.total_pages}</span>
              <Button variant="outline" size="sm" className="h-7" disabled={data.page >= data.total_pages}
                onClick={() => setPage(p => p + 1)} data-testid="adj-next-page"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </div>

      {adjustRow && canAdjust && (
        <LeaveAdjustmentDrawer employee={adjustRow} leaveTypeId={leaveTypeId} leaveYear={leaveYear} caps={caps}
          onClose={() => setAdjustRow(null)} onSaved={onAdjustmentSaved} />
      )}

      {historyRow && (
        <AdjustmentHistoryDrawer employee={historyRow} leaveTypeId={leaveTypeId} leaveYear={leaveYear}
          onClose={() => setHistoryRow(null)} />
      )}
    </div>
    </TooltipProvider>
  );
};

export default BalanceAdjustments;
